import {
  archiveTask,
  completeTask,
  resolveChecklist,
  createTask,
  editTask,
  fetchAllTasks,
  fetchTasksByEntity,
  fetchTasksById,
  fetchTasksByIds,
  markInProgressTask,
  reAssignTask,
  showTaskAddEditForm,
  bookmarkTask,
  fetchTaskChangelog,
  fetchPartialTasks,
  logTime,
  fetchMoreTasksByEntity,
  editEntityTask,
  completeEntityTask,
  clearEntityTasks,
  refreshEntityTask,
} from '../../routines/taskRoutines';
import { TASK_STATUS } from '../../../common/constants/TaskConstants';

const initialState = {
  // task taskList
  taskList: [],
  getTaskByIdsList: [],
  getTaskByIdsLoading: false,
  getTaskByIdsSuccess: false,
  partialTaskList: [],
  partialTaskListLoading: false,
  partialTaskListSuccess: false,
  paginationData: {},
  taskListLoading: false,
  taskListSuccess: false,
  tasksByEntity: [],
  tasksByEntityLoading: false,
  loadingMoreTasksByEntity: false,
  hasMoreEntityTasks: true,
  entityTasksPaginationData: {},
  refreshEntityTask: null,
  updatingTask: null,
  tasksByEntitySuccess: false,
  selectedTasks: {},
  selectedTasksLoading: {},
  selectedTasksSuccess: {},
  taskCompleting: false,
  taskResolving: false,
  taskResolvingSuccess: false,
  taskInProgressMarking: false,
  taskReAssigning: false,
  taskArchiving: false,
  taskCreating: false,
  taskCreateSuccess: false,
  taskUpdating: false,
  taskUpdateSuccess: false,
  showAddEditForm: false,
  addEditFormTaskId: null,
  taskBookmarking: false,
  currentPayload: null, // the payload used for the current result set
  dateBreakDown: {},
  timeLogging: false,
  timeLoggingSuccess: false,
};

const taskReducer = (state = initialState, action) => {
  switch (action.type) {
    case showTaskAddEditForm.TRIGGER:
      return {
        ...state,
        showAddEditForm: action.payload.showForm,
        addEditFormTaskId: action.payload.taskId,
      };

    // fetch tasks
    case fetchAllTasks.TRIGGER:
      return {
        ...state,
        taskListSuccess: false,
        taskListLoading: true,
      };
    case fetchAllTasks.SUCCESS: {
      const { response, currentPayload } = action.payload;
      return {
        ...state,
        taskListLoading: false,
        taskListSuccess: true,
        taskList: response.tasks || [],
        dateBreakDown: response.dateBreakDown || [],
        paginationData: {
          page: response.page,
          pageSize: response.pageSize,
          totalItems: response.totalItemCount,
        },
        currentPayload,
      };
    }
    case fetchAllTasks.FAILURE:
      return {
        ...state,
        taskListLoading: false,
        taskListSuccess: false,
      };

    // fetch partial tasks
    case fetchPartialTasks.TRIGGER:
      return {
        ...state,
        partialTaskListLoading: true,
      };
    case fetchPartialTasks.SUCCESS: {
      const response = action.payload;
      return {
        ...state,
        partialTaskList: response || [],
        partialTaskListLoading: false,
        partialTaskListSuccess: true,
      };
    }
    case fetchPartialTasks.FAILURE:
      return {
        ...state,
        partialTaskListLoading: false,
        partialTaskListSuccess: false,
      };

    // fetch tasks by ID
    case fetchTasksById.TRIGGER:
      return {
        ...state,
        selectedTasksLoading: {
          ...state.selectedTasksLoading,
          [action.payload?.taskId]: true,
        },
      };
    case fetchTasksById.SUCCESS:
      return {
        ...state,
        selectedTasksLoading: {
          ...state.selectedTasksLoading,
          [action.payload?.taskId]: false,
        },
        selectedTasksSuccess: {
          ...state.selectedTasksSuccess,
          [action.payload?.taskId]: true,
        },
        selectedTasks: {
          ...state.selectedTasks,
          [action.payload?.taskId]: action.payload?.response.data || null,
        },
      };
    case fetchTasksById.FAILURE:
      return {
        ...state,
        selectedTasksLoading: {
          ...state.selectedTasksLoading,
          [action.payload?.taskId]: false,
        },
        selectedTasksSuccess: {
          ...state.selectedTasksSuccess,
          [action.payload?.taskId]: false,
        },
      };

    case fetchMoreTasksByEntity.TRIGGER: {
      return {
        ...state,
        loadingMoreTasksByEntity: true,
      };
    }
    case fetchMoreTasksByEntity.SUCCESS: {
      const { data, ...paginationData } = action.payload;
      const hasMoreEntityTasks =
        paginationData.totalItemCount >
        state.tasksByEntity.length + data.length;
      return {
        ...state,
        tasksByEntity: [...state.tasksByEntity, ...data],
        entityTasksPaginationData: paginationData,
        loadingMoreTasksByEntity: false,
        hasMoreEntityTasks,
      };
    }
    case fetchMoreTasksByEntity.FAILURE: {
      return {
        ...state,
        loadingMoreTasksByEntity: false,
      };
    }

    case refreshEntityTask.TRIGGER: {
      return {
        ...state,
        refreshEntityTask: action.payload,
      };
    }

    // fetch tasks by IDs
    case fetchTasksByIds.TRIGGER:
      return {
        ...state,
        getTaskByIdsLoading: true,
      };
    case fetchTasksByIds.SUCCESS:
      return {
        ...state,
        getTaskByIdsList: action.payload?.response.data.tasks || [],
        getTaskByIdsLoading: false,
        getTaskByIdsSuccess: true,
      };
    case fetchTasksByIds.FAILURE:
      return {
        ...state,
        getTaskByIdsLoading: false,
        getTaskByIdsSuccess: false,
      };

    // fetch changelog by task ID
    case fetchTaskChangelog.TRIGGER:
      return {
        ...state,
        selectedTask: {
          ...state.selectedTask,
          changeLogLoading: true,
        },
      };
    case fetchTaskChangelog.SUCCESS:
      return {
        ...state,
        selectedTask: {
          ...state.selectedTask,
          changeLogLoading: false,
          changeLogSuccess: true,
          changeLog: action.payload?.data || [],
        },
      };
    case fetchTaskChangelog.FAILURE:
      return {
        ...state,
        selectedTask: {
          ...state.selectedTask,
          changeLogLoading: false,
          changeLogSuccess: false,
        },
      };

    // fetch tasks by entity
    case fetchTasksByEntity.TRIGGER:
      return {
        ...state,
        tasksByEntityLoading: true,
      };
    case fetchTasksByEntity.SUCCESS: {
      const { data, currentPayload, ...paginationData } = action.payload;
      const hasMoreEntityTasks = paginationData.totalItemCount > data.length;
      return {
        ...state,
        tasksByEntityLoading: false,
        tasksByEntitySuccess: true,
        tasksByEntity: data || [],
        hasMoreEntityTasks,
        entityTasksPaginationData: paginationData,
        currentPayload,
      };
    }
    case fetchTasksByEntity.FAILURE:
      return {
        ...state,
        tasksByEntityLoading: false,
        tasksByEntitySuccess: false,
      };

    // archive tasks
    case archiveTask.TRIGGER:
      return {
        ...state,
        taskArchiving: true,
      };
    case archiveTask.SUCCESS:
    case archiveTask.FAILURE:
      return {
        ...state,
        taskArchiving: false,
      };

    // complete tasks
    case completeTask.TRIGGER:
      return {
        ...state,
        taskCompleting: true,
      };
    case completeTask.SUCCESS:
    case completeTask.FAILURE:
      return {
        ...state,
        taskCompleting: false,
      };

    // resolve and complete tasks
    case resolveChecklist.TRIGGER:
      return {
        ...state,
        taskResolving: true,
      };
    case resolveChecklist.SUCCESS:
      return {
        ...state,
        taskResolving: false,
        taskResolvingSuccess: true,
      };
    case resolveChecklist.FAILURE:
      return {
        ...state,
        taskResolving: false,
        taskResolvingSuccess: false,
      };

    // mark in progress tasks
    case markInProgressTask.TRIGGER:
      return {
        ...state,
        taskInProgressMarking: true,
      };
    case markInProgressTask.SUCCESS:
    case markInProgressTask.FAILURE:
      return {
        ...state,
        taskInProgressMarking: false,
      };

    // reassigning tasks
    case reAssignTask.TRIGGER:
      return {
        ...state,
        taskReAssigning: true,
      };
    case reAssignTask.SUCCESS:
    case reAssignTask.FAILURE:
      return {
        ...state,
        taskReAssigning: false,
      };

    // bookmark tasks
    case bookmarkTask.TRIGGER: {
      const { taskId, formData } = action.payload;
      const { bookmarkStatus } = formData;
      const newEntityTaskList = [...state.tasksByEntity];
      const taskIndex = newEntityTaskList.findIndex(
        ({ data: task }) => task.taskId === taskId
      );
      const _updatingTask = newEntityTaskList[taskIndex];

      if (taskIndex !== -1) {
        const updatedTask = {
          ...newEntityTaskList[taskIndex].data,
          bookmarked: bookmarkStatus,
        };

        newEntityTaskList[taskIndex] = {
          ...newEntityTaskList[taskIndex],
          data: updatedTask,
        };
      }

      return {
        ...state,
        tasksByEntity: newEntityTaskList,
        taskBookmarking: true,
        updatingTask: _updatingTask,
      };
    }
    case bookmarkTask.SUCCESS: {
      return {
        ...state,
        taskBookmarking: false,
        updatingTask: null,
      };
    }
    case bookmarkTask.FAILURE: {
      const { taskId } = action.payload;
      const newEntityTaskList = [...state.tasksByEntity];
      const taskIndex = newEntityTaskList.findIndex(
        ({ data: task }) => task.taskId === taskId
      );

      const { updatingTask } = state;

      if (taskIndex !== -1) {
        const updatedTask = {
          ...newEntityTaskList[taskIndex].data,
          bookmarked: updatingTask.bookmarked,
        };
        newEntityTaskList[taskIndex] = {
          ...newEntityTaskList[taskIndex],
          data: updatedTask,
        };
      }

      return {
        ...state,
        tasksByEntity: newEntityTaskList,
        taskBookmarking: false,
        updatingTask: null,
      };
    }

    case editEntityTask.TRIGGER: {
      const { taskId, formData } = action.payload;
      const newEntityTaskList = [...state.tasksByEntity];
      const {
        name,
        assignee,
        creator,
        watchers,
        priority,
        status,
        description,
      } = formData;
      const newTaskData = {
        name,
        assignee,
        creator,
        watchers,
        priority,
        status,
        description,
      };
      const taskIndex = newEntityTaskList.findIndex(
        ({ data: task }) => task.taskId === taskId
      );

      const _updatingTask = newEntityTaskList[taskIndex];

      if (taskIndex !== -1) {
        const updatedTask = {
          ...newEntityTaskList[taskIndex].data,
          ...newTaskData,
        };
        newEntityTaskList[taskIndex] = {
          ...newEntityTaskList[taskIndex],
          data: updatedTask,
        };
      }

      return {
        ...state,
        tasksByEntity: newEntityTaskList,
        taskUpdating: true,
        updatingTask: _updatingTask,
      };
    }
    case editEntityTask.SUCCESS: {
      const data = action.payload.response;
      const { taskId } = data;
      const newEntityTaskList = [...state.tasksByEntity];

      const taskIndex = newEntityTaskList.findIndex(
        ({ data: task }) => task.taskId === taskId
      );

      if (taskIndex !== -1) {
        newEntityTaskList[taskIndex] = {
          ...newEntityTaskList[taskIndex],
          data,
        };
      }

      return {
        ...state,
        tasksByEntity: newEntityTaskList,
        taskUpdating: false,
        updatingTask: null,
      };
    }
    case editEntityTask.FAILURE: {
      const { taskId } = action.payload.data;
      const newEntityTaskList = [...state.tasksByEntity];
      const taskIndex = newEntityTaskList.findIndex(
        ({ data: task }) => task.taskId === taskId
      );

      const { updatingTask } = state;

      if (taskIndex !== -1) {
        newEntityTaskList[taskIndex] = {
          ...newEntityTaskList[taskIndex],
          data: updatingTask.data,
        };
      }

      return {
        ...state,
        tasksByEntity: newEntityTaskList,
        taskUpdating: false,
        updatingTask: null,
      };
    }

    case completeEntityTask.TRIGGER: {
      const { taskIds } = action.payload;
      const newEntityTaskList = [...state.tasksByEntity];
      const taskIndex = newEntityTaskList.findIndex(
        ({ data: task }) => task.taskId === taskIds[0]
      );

      const _updatingTask = newEntityTaskList[taskIndex];

      if (taskIndex !== -1) {
        const updatedTask = {
          ...newEntityTaskList[taskIndex].data,
          status: TASK_STATUS.COMPLETED,
        };
        newEntityTaskList[taskIndex] = {
          ...newEntityTaskList[taskIndex],
          data: updatedTask,
        };
      }

      return {
        ...state,
        tasksByEntity: newEntityTaskList,
        taskUpdating: true,
        updatingTask: _updatingTask,
      };
    }
    case completeEntityTask.SUCCESS:
      return {
        ...state,
        taskUpdating: false,
        updatingTask: null,
      };
    case completeEntityTask.FAILURE: {
      const { taskIds } = action.payload;
      const newEntityTaskList = [...state.tasksByEntity];
      const taskIndex = newEntityTaskList.findIndex(
        ({ data: task }) => task.taskId === taskIds[0]
      );

      const { updatingTask } = state;

      if (taskIndex !== -1) {
        newEntityTaskList[taskIndex] = {
          ...newEntityTaskList[taskIndex],
          data: updatingTask.data,
        };
      }

      return {
        ...state,
        tasksByEntity: newEntityTaskList,
        taskUpdating: false,
        updatingTask: null,
      };
    }

    case clearEntityTasks.TRIGGER: {
      return {
        ...state,
        tasksByEntity: [],
        tasksByEntityLoading: false,
        loadingMoreTasksByEntity: false,
        hasMoreEntityTasks: true,
        entityTasksPaginationData: {},
      };
    }

    // create tasks
    case createTask.TRIGGER:
      return {
        ...state,
        taskCreateSuccess: false,
        taskCreating: true,
      };
    case createTask.SUCCESS:
      return {
        ...state,
        taskCreateSuccess: true,
        taskCreating: false,
      };
    case createTask.FAILURE:
      return {
        ...state,
        taskCreateSuccess: false,
        taskCreating: false,
      };

    // update tasks
    case editTask.TRIGGER:
      return {
        ...state,
        taskUpdateSuccess: false,
        taskUpdating: true,
      };
    case editTask.SUCCESS:
      return {
        ...state,
        taskUpdateSuccess: true,
        taskUpdating: false,
      };
    case editTask.FAILURE:
      return {
        ...state,
        taskUpdateSuccess: false,
        taskUpdating: false,
      };

    // worklog
    case logTime.TRIGGER:
      return {
        ...state,
        timeLogging: true,
        timeLoggingSuccess: false,
      };
    case logTime.SUCCESS:
      return {
        ...state,
        timeLogging: false,
        timeLoggingSuccess: true,
      };
    case logTime.FAILURE:
      return {
        ...state,
        timeLogging: false,
        timeLoggingSuccess: false,
      };

    default:
      return state;
  }
};

export default taskReducer;
